import Joi from 'joi'
import {
  object as yupObject,
  string as yupString,
  array as yupArray,
  boolean as yupBoolean,
} from 'yup'

// const namePattern = /^[\p{L}\s'.,()-]+$/u
const namePattern = /^[^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{2,}$/
// const countryPattern = /^[A-Za-z\s.-]+$/
const alpha2Pattern = /[A-Za-z]{2}/

const fundsAvailableValues = [
  'Less than £50,000',
  '£50,000 to £100,000',
  '£100,000 to £200,000',
  'More than £200,000',
]

export const options = {
  fundsAvailableValues,
}

const clientSchemaBase = yupObject().shape({
  submitter: yupObject()
    .shape({
      name: yupString()
        .trim()
        .min(2, 'Too Short')
        .max(128, 'Too Long')
        .matches(namePattern, { message: 'Please only use letters' })
        .required('Required'),
      email: yupString()
        .trim()
        .email('Invalid email')
        .lowercase()
        .required('Required'),
      tel: yupString()
        .trim()
        .min(8, 'Too Short')
        .max(20, 'Too Long')
        .required('Required'),
    })
    .required(),
  countryOfOrigin: yupString()
    .trim()
    .length(2)
    .matches(alpha2Pattern)
    .required('Required'),
  enquiryType: yupObject()
    .shape({
      category: yupString().trim().min(2).max(255).required('Required'),
      services: yupArray().of(yupString().trim()).max(20),
    })
    .required('Required'),
  enquiry: yupString()
    .trim()
    .min(4, 'Too Short')
    .max(2000, 'Too Long')
    .required('Required'),
})

const clientSchemaAcknowledgements = yupObject().shape({
  acknowledgements: yupObject()
    .shape({
      englishLevel: yupBoolean(),
      fundsAvailable: yupString()
        .oneOf(fundsAvailableValues, 'Please select')
        .required('Required')
        .label('Funds available'),
    })
    .required(),
})

const initialValuesBase = {
  submitter: { name: '', email: '', tel: '' },
  countryOfOrigin: '',
  enquiryType: { category: '' },
  enquiry: '',
}

const attemptScreening = (formData, metaData) => {
  if (formData?.acknowledgements?.fundsAvailable === fundsAvailableValues[0])
    return false
  // if (formData.acknowledgements.englishLevel !== true) return false
  return true
}

export const clientForm = buildClientForm({ withAcknowledgements: false })

export function buildClientForm({ withAcknowledgements = true }) {
  let schema = clientSchemaBase
  let initialValues = initialValuesBase

  if (withAcknowledgements) {
    schema = schema.concat(clientSchemaAcknowledgements)
    initialValues = {
      ...initialValues,
      acknowledgements: { fundsAvailable: undefined, englishLevel: false },
    }
  }

  return {
    schema,
    initialValues,
    submitUrl: '/.netlify/functions/handle-form',
  }
}

const serverSchemaBase = Joi.object({
  submitter: Joi.object({
    name: Joi.string()
      .trim()
      .min(2)
      .max(128)
      .pattern(namePattern)
      .required()
      .label('Name'),
    email: Joi.string()
      .trim()
      .email({ tlds: false }) // for consistency with yup
      .lowercase()
      .required()
      .label('Email'),
    tel: Joi.string().trim().min(8).max(20).required().label('Telephone'),
  }).required(),
  countryOfOrigin: Joi.string()
    .trim()
    .length(2)
    .pattern(alpha2Pattern)
    .required()
    .label('Country of Origin'),
  enquiryType: Joi.object({
    category: Joi.string()
      .trim()
      .min(2)
      .max(255)
      .required()
      .label('Enquiry Category'),
    services: Joi.array()
      .items(Joi.string().trim().min(2).max(255).label('Service'))
      .max(20)
      .label('Enquiry Services'),
  }),
  enquiry: Joi.string().trim().min(4).max(2000).required().label('Enquiry'),
}).required()

const serverSchemaAcknowledgements = {
  acknowledgements: Joi.object({
    fundsAvailable: Joi.string()
      .valid(...fundsAvailableValues)
      .required(),
    englishLevel: Joi.boolean(),
  }).required(),
}

export function buildServerForm({ withAcknowledgements = true }) {
  let schema = serverSchemaBase

  if (withAcknowledgements) {
    schema = schema.append(serverSchemaAcknowledgements)
  }

  return {
    schema,
    attemptScreening,
  }
}

export const serverForm = buildServerForm({ withAcknowledgements: false })
